import React, { FC, RefObject, useEffect, useRef, useState } from 'react'
import {
	RadioList,
	RequestFormPDFProps,
	RequestInputFiles,
	RequestInputs
} from '@utils/request'
import requestForm from '@components/requestForms/request.form'
import { Collapse as ReactCollapse } from 'react-collapse'
import Collapse from '@components/ui/collapse'
import {
	REQUEST_TN_DETAILS_STEPS_NAME,
	requestCategories,
	Direction
} from '@services/constants'
import * as pageUtils from './__forms.utils'
import { useAppSelector } from '@services/store'
import TextInput from './inputs/text.inputs'
import DateFilterInput from './inputs/datefilter.input'
import { joinClasses, makeClasses } from '@utils/styles'
import { Colors } from '@utils/css-variables'
import RequestFiles from '@components/ui/requestFiles'
import { formatStrapiText, getFilesFromStore } from '@utils/methods'
import RadioInput from '@components/requestForms/inputs/radio.input'
import Notification from '@components/ui/notification'
import { ConcernFieldNames } from '@components/ui/concernedInfosSection/__index.utils'
import ConcernedInfosSection from '@components/ui/concernedInfosSection'
import { getAddressByCoordinates } from '@components/ui/addressForm/__index.utils'
import { AddressCoordinates } from '@services/models'
import { elderSubventionNumClientExample } from '@images/icons'

const ElderSubventionRenewalForm: FC<RequestFormPDFProps> = ({
	inputs,
	errors,
	subSteps,
	isSummaryStep,
	steps,
	toPrint,
	filesInputs,
	onSetInputFiles,
	onSetSubSteps,
	onFixError,
	onSetInputs,
	setCurrentStep,
	onSetCurrentSubStep
}) => {
	const {
		authUser,
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputFilesArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	type Classes = {
		button: string
		adresseBlock: string
		outlinedButton: string
		btnPadding: string
		greyBackgroundBlock: string
		checkbox2Container: string
		checkboxWrapper: string
		checkbox2: string
		checkbox2Label: string
		clientNumberExampleImageWrapper: string
		clientNumberExampleImage: string
	}

	const classes: Classes = makeClasses({
		button: {
			display: 'inline-block',
			padding: '10px 20px',
			fontSize: '13px',
			fontWeight: '500',
			color: Colors.white,
			lineHeight: '1em',
			borderRadius: '23px',
			transition: 'all 0.35s ease',
			MsTransition: 'all 0.35s ease',
			background: Colors.secondary,
			border: 'none',
			cursor: 'pointer',
			'&:hover': {
				background: Colors.darkBlue2,
				borderColor: Colors.darkBlue2,
				color: Colors.white
			}
		},
		adresseBlock: {
			border: `1px solid ${Colors.greyAlto}`,
			padding: '20px 25px',
			marginBottom: '10px'
		},
		checkbox2: {
			width: 'fit-content',
			position: 'absolute',
			verticalAlign: 'baseline',
			clip: 'rect(1px, 1px, 1px, 1px)'
		},
		checkbox2Label: {
			fontSize: '16px',
			padding: '0',
			color: Colors.lightBlack,
			position: 'relative',
			paddingLeft: '27px',
			fontWeight: 'normal',
			cursor: 'pointer',
			lineHeight: '17px',
			':before': {
				content: "''",
				position: 'absolute',
				top: '0',
				left: '0',
				width: '16px',
				height: '16px',
				border: `1px solid ${Colors.lightGrey3}`,
				background: Colors.white,
				outline: 'none !important'
			},
			':after': {
				content: "''",
				position: 'absolute',
				top: '4px',
				left: '4px',
				width: '10px',
				height: '10px',
				background: Colors.secondary,
				outline: 'none',
				opacity: '0',
				transition: 'all .15s ease-out'
			},

			'&.error-check': {
				color: `${Colors.red} !important`,
				':before': {
					border: `1px solid ${Colors.red}`
				}
			}
		},
		checkbox2Container: {
			'& input[value=true] ~ label:after': {
				opacity: '.99'
			}
		},
		checkboxWrapper: {
			display: 'block',
			margin: '0 0 5px 0',
			background: Colors.ligthGrey2,
			border: `1px solid ${Colors.greyAlto}`,
			padding: '14px 12px'
		},
		clientNumberExampleImageWrapper: {
			border: '1px solid #ddd',
			padding: '10px',
			marginTop: '15px',
			marginBottom: '20px',
			maxWidth: '600px'
		},
		clientNumberExampleImage: {
			maxWidth: '100%',
			height: 'auto',
			display: 'block'
		}
	})

	const answers = useAppSelector((state) => state.request.configs)

	const radioListFirstTimeSubmit: RadioList[] = [
		{
			label:
				pageAssets.request_form_elderSubvention_firstTimeSubmit_answerYes_label,
			value: 'true',
			key: 'request_form_elderSubvention_firstTimeSubmit_answerYes_label'
		},
		{
			label:
				pageAssets.request_form_elderSubvention_firstTimeSubmit_answerNo_label,
			value: 'false',
			key: 'request_form_elderSubvention_firstTimeSubmit_answerNo_label'
		}
	]

	const radioListIncomeSupplement: RadioList[] = [
		{
			label:
				pageAssets.request_form_elderSubvention_incomeSupplement_answerYes_label,
			value: 'true',
			key: 'request_form_elderSubvention_incomeSupplement_answerYes_label'
		},
		{
			label:
				pageAssets.request_form_elderSubvention_incomeSupplement_answerNo_label,
			value: 'false',
			key: 'request_form_elderSubvention_incomeSupplement_answerNo_label'
		}
	]

	const radioListYesOrNo: RadioList[] = [
		{
			label: pageAssets.label_yes,
			value: 'true',
			key: 'label_yes'
		},
		{
			label: pageAssets.label_no,
			value: 'false',
			key: 'label_no'
		}
	]

	const isProofOfOccupationRequired = (
		firstTimeSubmitValue?: string | boolean,
		isClientHouseOwnerValue?: string | boolean
	) => {
		return (
			`${firstTimeSubmitValue}` === 'false' &&
			`${isClientHouseOwnerValue}` === 'false'
		)
	}

	//----------------------------------------------------------------
	//-------------------- DESCRIPTION INPUTS ------------------------
	//----------------------------------------------------------------
	// Main RequestInputs (text/select/number) managed locally in this component.
	// Each RequestInputs state mirrors a field that will be sent to the parent
	// request form manager via onSetInputs. They typically include a `ref`,
	// localized label and a `required` flag that can change dynamically.

	// Pour qui faites-vous cette demande?
	const [firstTimeSubmitInput, setFirstTimeSubmitInput] =
		useState<RequestInputs>({
			name: 'firstTimeSubmit',
			label: pageAssets?.request_form_elderSubvention_firstTimeSubmit_label,
			labelKey: 'request_form_elderSubvention_firstTimeSubmit_label',
			value: answers.firstTimeSubmit?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null)
		})

	const [clientNumberInput, setClientNumberInput] = useState<RequestInputs>({
		name: 'clientNumber',
		label: pageAssets?.request_form_elderSubvention_clientNumber_label,
		labelKey: 'pageAssets?.request_form_elderSubvention_clientNumber_label',
		value: answers?.clientNumber?.toString() || '',
		required: answers?.firstTimeSubmit || false,
		ref: useRef<HTMLInputElement>(null)
	})

	const [clientAgeInput, setClientAgeInput] = useState<RequestInputs>({
		name: 'clientAge',
		label: pageAssets?.request_form_elderSubvention_clientAge_label,
		labelKey: 'pageAssets?.request_form_elderSubvention_clientAge_label',
		description: pageAssets?.request_form_elderSubvention_clientAge_description,
		value: answers.clientAge || '',
		required: !answers.firstTimeSubmit || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
	})

	const [buildingBatchNumberInput, setBuildingBatchNumberInput] =
		useState<RequestInputs>({
			name: 'buildingBatchNumber',
			label: '',
			labelKey: '',
			value: answers.buildingBatchNumber || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		})

	const [buildingRegistrationNumberInput, setBuildingRegistrationNumberInput] =
		useState<RequestInputs>({
			name: 'buildingRegistrationNumber',
			label: '',
			labelKey: '',
			value: answers.buildingRegistrationNumber || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		})

	const [clientCorrectAddressInput, setClientCorrectAddressInput] =
		useState<RequestInputs>({
			name: 'clientCorrectAddress',
			label:
				pageAssets?.request_form_elderSubvention_clientCorrectAddress_label ||
				'Question',
			labelKey: 'request_form_elderSubvention_clientCorrectAddress_label',
			value: answers.clientCorrectAddress?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		})

	const [isClientHouseOwnerInput, setIsClientHouseOwnerInput] =
		useState<RequestInputs>({
			name: 'isClientHouseOwner',
			label: pageAssets?.request_form_elderSubvention_isClientHouseOwner_label,
			labelKey: 'request_form_elderSubvention_isClientHouseOwner_label',
			value: answers.isClientHouseOwner?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		})

	const [moveDateInput, setMoveDateInput] = useState<RequestInputs>({
		name: 'moveDate',
		label:
			pageAssets?.request_form_elderSubvention_moveDate_radio_label ||
			'Question',
		labelKey: 'request_form_elderSubvention_moveDate_radio_label',
		value: answers.moveDate || '',
		required:
			(!answers.clientCorrectAddress && answers.firstTimeSubmit) || false,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
	})

	const [incomeSupplementRadio, setIncomeSupplementRadio] =
		useState<RequestInputs>({
			name: 'incomeSupplementRadio',
			label: pageAssets?.request_form_elderSubvention_income_supplement_label,
			labelKey: 'request_form_elderSubvention_income_supplement_label',
			description:
				pageAssets?.request_form_elderSubvention_income_supplement_description_label,
			value: answers.incomeSupplementRadio?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})

	const [incomeSupplementText, setIncomeSupplementText] =
		useState<RequestInputs>({
			name: 'incomeSupplementText',
			label:
				pageAssets?.request_form_elderSubvention_income_supplement_text_label,
			labelKey: 'request_form_elderSubvention_income_supplement_text_label',
			value: isNaN(Number(answers.incomeSupplementText))
				? ''
				: `${answers.incomeSupplementText}`,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})

	const [incomeSupplementTextCustomError, setIncomeSupplementTextCustomError] =
		useState<string>('')

	const [details3CoBeneficiaryRadio, setDetails3CoBeneficiaryRadio] =
		useState<RequestInputs>({
			name: 'coBeneficiaryRadio',
			label:
				pageAssets?.request_form_elderSubvention_co_beneficiary_radio_label,
			labelKey: 'request_form_elderSubvention_co_beneficiary_radio_label',
			value: answers.coBeneficiaryRadio?.toString() || '',
			required: true,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})

	const [details3CoBeneficiaryName, setDetails3CoBeneficiaryName] =
		useState<RequestInputs>({
			name: 'coBeneficiaryFirstName',
			label:
				pageAssets?.request_form_elderSubvention_co_beneficiary_firstname_label,
			labelKey: 'request_form_elderSubvention_co_beneficiary_firstname_label',
			value: answers.coBeneficiaryFirstName || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})

	const [details3CoBeneficiaryRelation, setDetails3CoBeneficiaryRelation] =
		useState<RequestInputs>({
			name: 'coBeneficiaryLastName',
			label:
				pageAssets?.request_form_elderSubvention_co_beneficiary_lastname_label,
			labelKey: 'request_form_elderSubvention_co_beneficiary_lastname_label',
			value: answers.coBeneficiaryLastName || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})

	const [details3CoBeneficiaryDate, setDetails3CoBeneficiaryDate] =
		useState<RequestInputs>({
			name: 'coBeneficiaryDate',
			label: pageAssets?.request_form_elderSubvention_co_beneficiary_date_label,
			labelKey: 'request_form_elderSubvention_co_beneficiary_date_label',
			value: answers.coBeneficiaryDate || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		})
	//----------------------------------------------------------------
	//----------------------- DOCUMENTS INPUTS -----------------------
	//----------------------------------------------------------------
	// File inputs (RequestInputFiles) are stored locally as sets of File objects
	// and synchronized with the parent using onSetInputFiles. They also carry
	// metadata (category, required, numberFilesRequired) used by the
	// RequestFiles component for validation and display.

	const [proofOfBirthInput, setproofOfBirthInput] = useState<RequestInputFiles>(
		{
			name: 'proofOfBirthInput',
			label: pageAssets?.request_form_elderSubvention_proofOfBirth_label,
			labelKey: 'request_form_elderSubvention_proofOfBirth_label',
			description:
				pageAssets?.request_form_tarifs_reduction_proofOfResidency_description,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'proofOfBirthInput')?.files || []
			),
			required: `${answers?.firstTimeSubmit}` === 'false',
			numberFilesRequired: 1,
			category: requestCategories.proofOfBirth,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		}
	)

	const [proofOfResidencyInput, setProofOfResidencyInput] =
		useState<RequestInputFiles>({
			name: 'proofOfResidencyInput',
			label: pageAssets?.request_form_elderSubvention_proofOfResidency_label,
			labelKey: 'request_form_elderSubvention_proofOfResidency_label',
			description:
				pageAssets?.request_form_elderSubvention_proofOfResidency_description_lablel,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'proofOfResidencyInput')?.files ||
					[]
			),
			required: true,
			numberFilesRequired: 1,
			category: requestCategories.proofOfResidency,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		})

	const [renewalFormRenter, setRenewalFormRenter] = useState<RequestInputFiles>(
		{
			name: 'renewalFormRenterInput',
			label: pageAssets?.request_form_elderSubvention_renewal_form_renter_label,
			labelKey: 'request_form_elderSubvention_renewal_form_renter_label',
			description:
				pageAssets?.request_form_elderSubvention_renewal_form_renter_description_label,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'renewalFormRenterInput')?.files || []
			),
			required: false,
			numberFilesRequired: 1,
			category: requestCategories.paperForm,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		}
	)

	const [renewalFormOwner, setRenewalFormOwner] = useState<RequestInputFiles>({
		name: 'renewalFormOwnerInput',
		label: pageAssets?.request_form_elderSubvention_renewal_form_owner_label,
		labelKey: 'request_form_elderSubvention_renewal_form_owner_label',
		description:
			pageAssets?.request_form_elderSubvention_renewal_form_owner_description_label,
		files: new Set<File>(
			filesInputs?.find((el) => el.name == 'renewalFormOwnerInput')?.files || []
		),
		required: false,
		numberFilesRequired: 1,
		category: requestCategories.paperForm,
		shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
	})

	const [proofOfOccupation, setProofOfOccupation] = useState<RequestInputFiles>(
		{
			name: 'proofOfOccupationInput',
			label:
				pageAssets?.request_form_elderSubvention_renewal_form_proofOfOccupation_label,
			labelKey:
				'request_form_elderSubvention_renewal_form_proofOfOccupation_label',
			description:
				pageAssets?.request_form_elderSubvention_renewal_form_proofOfOccupation_description_label,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'proofOfOccupationInput')?.files || []
			),
			required: isProofOfOccupationRequired(
				answers?.firstTimeSubmit,
				answers?.isClientHouseOwner
			),
			numberFilesRequired: 1,
			category: requestCategories.proofOfResidency,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		}
	)

	const [incomeSupplementFile, setIncomeSupplementFile] =
		useState<RequestInputFiles>({
			name: 'incomeSupplementFileInput',
			label:
				pageAssets?.request_form_elderSubvention_income_supplement_file_label,
			labelKey:
				'request_form_elderSubvention_details3_income_supplement_file_label',
			description:
				pageAssets?.request_form_elderSubvention_income_supplement_file_description_label,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'incomeSupplementFileInput')?.files ||
					[]
			),
			required: false,
			numberFilesRequired: 1,
			category: requestCategories.eighthInput,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		})

	const [details3CoBeneficiaryFile, setDetails3CoBeneficiaryFile] =
		useState<RequestInputFiles>({
			name: 'coBeneficiaryFileInput',
			label: pageAssets?.request_form_elderSubvention_co_beneficiary_file_label,
			labelKey: 'request_form_elderSubvention_co_beneficiary_file_label',
			description:
				pageAssets?.request_form_elderSubvention_co_beneficiary_file_description_label,
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'coBeneficiaryFileInput')?.files || []
			),
			required: false,
			numberFilesRequired: 1,
			category: requestCategories.identityDocument,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
		})

	// ------------- DETAILS INPUTS SETTERS ----------------

	const onSelectFirstTimeSubmitInput = (value: string) => {
		let required = value == 'true'

		setFirstTimeSubmitInput({ ...firstTimeSubmitInput, value })

		setClientAgeInput({ ...clientAgeInput, required: !required })

		setClientNumberInput({ ...clientNumberInput, required: required || false })

		setClientCorrectAddressInput({
			...clientCorrectAddressInput,
			required: required
		})

		setMoveDateInput({
			...moveDateInput,
			required: required
		})

		setproofOfBirthInput({
			...proofOfBirthInput,
			required: !required
		})

		setProofOfResidencyInput({
			...proofOfResidencyInput,
			required:
				value === 'false' ||
				(clientCorrectAddressInput.value === 'false' && value === 'true') ||
				(isClientHouseOwnerInput.value === 'false' && value === 'true')
		})

		setRenewalFormRenter({
			...renewalFormRenter,
			required: required
		})

		setRenewalFormOwner({
			...renewalFormOwner,
			required: required
		})

		setProofOfOccupation({
			...proofOfOccupation,
			required: isProofOfOccupationRequired(
				value,
				isClientHouseOwnerInput.value
			)
		})

		onFixError(
			errors.filter(
				(error) =>
					![
						firstTimeSubmitInput.name,
						clientNumberInput.name,
						clientAgeInput.name,
						proofOfBirthInput.name,
						clientCorrectAddressInput.name,
						moveDateInput.name,
						proofOfResidencyInput.name,
						renewalFormRenter.name,
						renewalFormOwner.name,
						proofOfOccupation.name
					].includes(error)
			)
		)
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, firstTimeSubmitInput))
	}, [firstTimeSubmitInput])

	const onSelectClientNumberInput = (value: string) => {
		if (isNaN(Number(value)) || value.length > 6) return
		setClientNumberInput({ ...clientNumberInput, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, clientNumberInput.name))
	}

	const onSelectClientAgeInput = (value: string) => {
		setClientAgeInput({ ...clientAgeInput, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, clientAgeInput.name))
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, clientNumberInput))
	}, [clientNumberInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, clientAgeInput))
	}, [clientAgeInput])

	const onSelectProofOfBirthInput = (files: Set<File>) => {
		setproofOfBirthInput({
			...proofOfBirthInput,
			files
		})
		onFixError(UpdateRequestInputsErrorsArray(errors, proofOfBirthInput.name))
	}

	const onSelectProofOfResidencyInput = (files: Set<File>) => {
		setProofOfResidencyInput({
			...proofOfResidencyInput,
			files
		})
		onFixError(
			UpdateRequestInputsErrorsArray(errors, proofOfResidencyInput.name)
		)
	}

	const onSelectRenewalFormRenter = (files: Set<File>) => {
		setRenewalFormRenter({
			...renewalFormRenter,
			files
		})
		onFixError(UpdateRequestInputsErrorsArray(errors, renewalFormRenter.name))
	}

	const onSelectRenewalFormOwner = (files: Set<File>) => {
		setRenewalFormOwner({
			...renewalFormOwner,
			files
		})
		onFixError(UpdateRequestInputsErrorsArray(errors, renewalFormOwner.name))
	}

	const onSelectProofOfOccupation = (files: Set<File>) => {
		setProofOfOccupation({
			...proofOfOccupation,
			files
		})
		onFixError(UpdateRequestInputsErrorsArray(errors, proofOfOccupation.name))
	}

	const getCadastralAndMatriculeNumber = (coordinates: AddressCoordinates) => {
		getAddressByCoordinates(coordinates).then((res) => {
			setBuildingBatchNumberInput({
				...buildingBatchNumberInput,
				value: res?.data?.features?.[0].properties.cadastre
			})

			setBuildingRegistrationNumberInput({
				...buildingRegistrationNumberInput,
				value: res?.data?.features?.[0].properties.matricule
			})
		})
	}
	const onSelectIsClientHouseOwnerInput = (value: string) => {
		setIsClientHouseOwnerInput({ ...isClientHouseOwnerInput, value })
		setProofOfResidencyInput({
			...proofOfResidencyInput,
			required:
				firstTimeSubmitInput.value === 'false' ||
				(clientCorrectAddressInput.value === 'false' &&
					firstTimeSubmitInput.value === 'true') ||
				(value === 'false' && firstTimeSubmitInput.value === 'true')
		})
		setRenewalFormRenter({
			...renewalFormRenter,
			required: value === 'false' && firstTimeSubmitInput.value === 'true'
		})
		setRenewalFormOwner({
			...renewalFormOwner,
			required: value === 'true' && firstTimeSubmitInput.value === 'true'
		})
		setProofOfOccupation({
			...proofOfOccupation,
			required: isProofOfOccupationRequired(firstTimeSubmitInput.value, value)
		})
		onFixError(
			errors.filter(
				(error) =>
					![
						isClientHouseOwnerInput.name,
						proofOfResidencyInput.name,
						renewalFormRenter.name,
						renewalFormOwner.name,
						proofOfOccupation.name
					].includes(error)
			)
		)
	}
	const onSelectClientCorrectAddressInput = (value: string) => {
		setClientCorrectAddressInput({
			...clientCorrectAddressInput,
			value
		})
		setProofOfResidencyInput({
			...proofOfResidencyInput,
			required:
				firstTimeSubmitInput.value === 'false' ||
				(value === 'false' && firstTimeSubmitInput.value === 'true') ||
				(isClientHouseOwnerInput.value === 'false' &&
					firstTimeSubmitInput.value === 'true')
		})

		setMoveDateInput({
			...moveDateInput,
			required: value === 'false'
		})

		onFixError(
			errors.filter(
				(error) =>
					![
						clientCorrectAddressInput.name,
						proofOfResidencyInput.name,
						moveDateInput.name
					].includes(error)
			)
		)
	}

	const onSelectMoveDateInput = (value: string) => {
		setMoveDateInput({ ...moveDateInput, value })
		onFixError(UpdateRequestInputsErrorsArray(errors, moveDateInput.name))
	}

	const onSelectIncomeSupplementRadio = (value: string) => {
		const isRequired = value === 'true'
		setIncomeSupplementRadio({
			...incomeSupplementRadio,
			value
		})
		setIncomeSupplementText({
			...incomeSupplementText,
			required: isRequired
		})
		setIncomeSupplementFile({
			...incomeSupplementFile,
			required: isRequired
		})
		onFixError(
			errors.filter(
				(error) =>
					![
						incomeSupplementRadio.name,
						incomeSupplementText.name,
						incomeSupplementFile.name
					].includes(error)
			)
		)
	}

	const onSelectIncomeSupplementText = (value: string) => {
		if (isNaN(Number(value)) || value.includes('.') || value.length > 6) return
		setIncomeSupplementText({
			...incomeSupplementText,
			value
		})
		const numValue = Number(value)
		if (value.trim() !== '' && numValue <= 0) {
			if (!errors.includes(incomeSupplementText.name)) {
				onFixError((oldErrors) => [...oldErrors, incomeSupplementText.name])
			}
			setIncomeSupplementTextCustomError(
				'Le montant à la case 21 doit être plus grand que 0 pour être admissible au supplément de revenu garanti'
			)
		} else {
			onFixError(
				UpdateRequestInputsErrorsArray(errors, incomeSupplementText.name)
			)
			setIncomeSupplementTextCustomError('')
		}
	}

	const onSelectIncomeSupplementFile = (files: Set<File>) => {
		setIncomeSupplementFile({
			...incomeSupplementFile,
			files
		})
		onFixError(
			UpdateRequestInputsErrorsArray(errors, incomeSupplementFile.name)
		)
	}

	const onSelectDetails3CoBeneficiaryRadio = (value: string) => {
		const isRequired = value === 'true'
		setDetails3CoBeneficiaryRadio({ ...details3CoBeneficiaryRadio, value })
		setDetails3CoBeneficiaryName({
			...details3CoBeneficiaryName,
			required: isRequired
		})
		setDetails3CoBeneficiaryRelation({
			...details3CoBeneficiaryRelation,
			required: isRequired
		})
		setDetails3CoBeneficiaryDate({
			...details3CoBeneficiaryDate,
			required: isRequired
		})
		setDetails3CoBeneficiaryFile({
			...details3CoBeneficiaryFile,
			required: isRequired
		})
		onFixError(
			errors.filter(
				(error) =>
					![
						details3CoBeneficiaryRadio.name,
						details3CoBeneficiaryName.name,
						details3CoBeneficiaryRelation.name,
						details3CoBeneficiaryDate.name,
						details3CoBeneficiaryFile.name
					].includes(error)
			)
		)
	}

	const onSelectDetails3CoBeneficiaryName = (value: string) => {
		setDetails3CoBeneficiaryName({ ...details3CoBeneficiaryName, value })
		onFixError(
			UpdateRequestInputsErrorsArray(errors, details3CoBeneficiaryName.name)
		)
	}

	const onSelectDetails3CoBeneficiaryRelation = (value: string) => {
		setDetails3CoBeneficiaryRelation({
			...details3CoBeneficiaryRelation,
			value
		})
		onFixError(
			UpdateRequestInputsErrorsArray(errors, details3CoBeneficiaryRelation.name)
		)
	}

	const onSelectDetails3CoBeneficiaryDate = (value: string) => {
		setDetails3CoBeneficiaryDate({ ...details3CoBeneficiaryDate, value })
		onFixError(
			UpdateRequestInputsErrorsArray(errors, details3CoBeneficiaryDate.name)
		)
	}

	const onSelectDetails3CoBeneficiaryFile = (files: Set<File>) => {
		setDetails3CoBeneficiaryFile({
			...details3CoBeneficiaryFile,
			files
		})
		onFixError(
			UpdateRequestInputsErrorsArray(errors, details3CoBeneficiaryFile.name)
		)
	}

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, proofOfBirthInput)
		)
	}, [proofOfBirthInput])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, proofOfResidencyInput)
		)
	}, [proofOfResidencyInput])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, renewalFormRenter)
		)
	}, [renewalFormRenter])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, renewalFormOwner)
		)
	}, [renewalFormOwner])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, proofOfOccupation)
		)
	}, [proofOfOccupation])

	useEffect(() => {
		const shouldRequireProofOfOccupation = isProofOfOccupationRequired(
			firstTimeSubmitInput.value,
			isClientHouseOwnerInput.value
		)

		setProofOfOccupation((prev) => {
			if (prev.required === shouldRequireProofOfOccupation) {
				return prev
			}

			return {
				...prev,
				required: shouldRequireProofOfOccupation
			}
		})
	}, [firstTimeSubmitInput.value, isClientHouseOwnerInput.value])

	useEffect(() => {
		// check if exist answers (filesInputs) at the store, to intialize the input with the files already present
		// ----------------------------
		// firstInput input
		if (!!answers.proofOfResidency && proofOfBirthInput.files.size == 0) {
			getFilesFromStore('proofOfResidency', answers).then(
				(result: Set<File>) => {
					setproofOfBirthInput({
						...proofOfBirthInput,
						files: result
					})
				}
			)
		}
	}, [])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, buildingBatchNumberInput))
	}, [buildingBatchNumberInput])

	useEffect(() => {
		onSetInputs(
			UpdateRequestInputsArray(inputs, buildingRegistrationNumberInput)
		)
	}, [buildingRegistrationNumberInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, isClientHouseOwnerInput))
	}, [isClientHouseOwnerInput])
	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, clientCorrectAddressInput))
	}, [clientCorrectAddressInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, moveDateInput))
	}, [moveDateInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, incomeSupplementRadio))
	}, [incomeSupplementRadio])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, incomeSupplementText))
	}, [incomeSupplementText])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, incomeSupplementFile)
		)
	}, [incomeSupplementFile])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, details3CoBeneficiaryRadio))
	}, [details3CoBeneficiaryRadio])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, details3CoBeneficiaryName))
	}, [details3CoBeneficiaryName])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, details3CoBeneficiaryRelation))
	}, [details3CoBeneficiaryRelation])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, details3CoBeneficiaryDate))
	}, [details3CoBeneficiaryDate])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, details3CoBeneficiaryFile)
		)
	}, [details3CoBeneficiaryFile])

	const onSetErrorFromFileComponent = (
		hasErrorInComponent: boolean,
		inputName: string
	) => {
		if (hasErrorInComponent) {
			if (!errors.includes(inputName)) {
				onFixError((oldErrors) => [...oldErrors, inputName])
			}

			return
		}

		onFixError(UpdateRequestInputsErrorsArray(errors, inputName))
	}

	useEffect(() => {
		onSetSubSteps({
			map: false,
			details_1: true,
			details_2: true,
			details_3: true,
			requiredDocument: false,
			position:
				subSteps?.position && String(subSteps?.position) !== ''
					? subSteps?.position
					: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
			steps: [
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2,
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
			]
		})
	}, [])

	const backToForm = (step: number, subStep: string) => {
		if (setCurrentStep && onSetSubSteps && subSteps) {
			setCurrentStep(step)
			onSetSubSteps({
				...subSteps,
				position: subStep as REQUEST_TN_DETAILS_STEPS_NAME
			})
		}
	}

	// Calculate maxDate for age input: 65 years ago from May 1st
	const currentYear = new Date().getFullYear()
	const maxDateFor65Years = new Date(currentYear - 65, 4, 1)

	// Summary / print view
	// When isSummaryStep is true the component renders a read-only summary of
	// the previously entered values (answers from the store) and the uploaded
	// files. Interactive inputs are hidden but a "modify" link is provided to
	// jump back to the form sub-step.
	if (isSummaryStep) {
		return (
			<section
				className={pageUtils.classes.noMargin}
				style={{ padding: '0px' }}
			>
				{subSteps?.details_1 && (
					<>
						{!toPrint && (
							<div>
								<a
									style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
									className={joinClasses([
										pageUtils.classes.btn,
										pageUtils.classes.button,
										pageUtils.classes.buttonOutline
									])}
									onClick={() =>
										backToForm(
											steps?.form!,
											REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
										)
									}
								>
									{pageAssets?.label_modify}
								</a>

								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_elderSubvention_firstSubStep_title}
								</h4>
							</div>
						)}

						<div>
							<strong>{firstTimeSubmitInput.label}</strong>
							<span
								className={joinClasses([
									pageUtils.classes.answerURB,
									'answerURB'
								])}
							>
								{answers.firstTimeSubmit
									? pageAssets.label_yes
									: pageAssets.label_no}
							</span>
						</div>

						{answers.firstTimeSubmit && (
							<div>
								<strong>{clientNumberInput.label}</strong>
								<span
									className={joinClasses([
										pageUtils.classes.answerURB,
										'answerURB'
									])}
								>
									{`${answers.clientNumber}` || 'N/A'}
								</span>
							</div>
						)}

						{!answers.firstTimeSubmit && (
							<>
								<div>
									<strong>{clientAgeInput.label}</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.clientAge || 'N/A'}
									</span>
								</div>
								<RequestFiles
									subtitle={proofOfBirthInput.label}
									name={proofOfBirthInput.name}
									required={false}
									files={proofOfBirthInput.files}
									hasError={false}
									onSetHasError={() => {}}
									onSetFiles={() => {}}
									maxFiles={proofOfBirthInput.numberFilesRequired}
									removePadding
									isSummary={true}
								/>
							</>
						)}
					</>
				)}

				{subSteps?.details_2 && (
					<>
						{!toPrint && (
							<div>
								<a
									style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
									className={joinClasses([
										pageUtils.classes.btn,
										pageUtils.classes.button,
										pageUtils.classes.buttonOutline
									])}
									onClick={() =>
										backToForm(
											steps?.form!,
											REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
										)
									}
								>
									{pageAssets?.label_modify}
								</a>

								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_parkingPermit_address_of_request}
								</h4>
							</div>
						)}

						{answers.firstTimeSubmit && (
							<div>
								<strong>{clientCorrectAddressInput.label}</strong>
								<span
									className={joinClasses([
										pageUtils.classes.answerURB,
										'answerURB'
									])}
								>
									{answers.clientCorrectAddress
										? pageAssets.label_yes
										: pageAssets.label_no}
								</span>
							</div>
						)}

						{(!answers.clientCorrectAddress || !answers.firstTimeSubmit) && (
							<>
								<div>
									<strong>Adresse</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.concernedAdresse || 'N/A'}
									</span>
								</div>
								<div>
									<strong>Appartement</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.concernedAppartement || 'N/A'}
									</span>
								</div>
							</>
						)}

						{!answers.clientCorrectAddress && answers.firstTimeSubmit && (
							<div>
								<strong>{moveDateInput.label}</strong>
								<span
									className={joinClasses([
										pageUtils.classes.answerURB,
										'answerURB'
									])}
								>
									{answers.moveDate || 'N/A'}
								</span>
							</div>
						)}

						<div>
							<strong>{isClientHouseOwnerInput.label}</strong>
							<span
								className={joinClasses([
									pageUtils.classes.answerURB,
									'answerURB'
								])}
							>
								{answers.isClientHouseOwner
									? pageAssets.label_yes
									: pageAssets.label_no}
							</span>
						</div>

						{(!answers.clientCorrectAddress ||
							!answers.isClientHouseOwner ||
							!answers.firstTimeSubmit) && (
							<RequestFiles
								subtitle={proofOfResidencyInput.label}
								name={proofOfResidencyInput.name}
								required={false}
								files={proofOfResidencyInput.files}
								hasError={false}
								onSetHasError={() => {}}
								onSetFiles={() => {}}
								maxFiles={proofOfResidencyInput.numberFilesRequired}
								removePadding
								isSummary={true}
							/>
						)}

						{answers.firstTimeSubmit && answers.isClientHouseOwner && (
							<RequestFiles
								subtitle={renewalFormOwner.label}
								name={renewalFormOwner.name}
								required={false}
								files={renewalFormOwner.files}
								hasError={false}
								onSetHasError={() => {}}
								onSetFiles={() => {}}
								maxFiles={renewalFormOwner.numberFilesRequired}
								removePadding
								isSummary={true}
							/>
						)}

						{answers.firstTimeSubmit && !answers.isClientHouseOwner && (
							<RequestFiles
								subtitle={renewalFormRenter.label}
								name={renewalFormRenter.name}
								required={false}
								files={renewalFormRenter.files}
								hasError={false}
								onSetHasError={() => {}}
								onSetFiles={() => {}}
								maxFiles={renewalFormRenter.numberFilesRequired}
								removePadding
								isSummary={true}
							/>
						)}

						{!answers.firstTimeSubmit && !answers.isClientHouseOwner && (
							<RequestFiles
								subtitle={proofOfOccupation.label}
								name={proofOfOccupation.name}
								required={false}
								files={proofOfOccupation.files}
								hasError={false}
								onSetHasError={() => {}}
								onSetFiles={() => {}}
								maxFiles={proofOfOccupation.numberFilesRequired}
								removePadding
								isSummary={true}
							/>
						)}
					</>
				)}

				{subSteps?.details_3 && (
					<>
						{!toPrint && (
							<div>
								<a
									style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
									className={joinClasses([
										pageUtils.classes.btn,
										pageUtils.classes.button,
										pageUtils.classes.buttonOutline
									])}
									onClick={() =>
										backToForm(
											steps?.form!,
											REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
										)
									}
								>
									{pageAssets?.label_modify}
								</a>

								<h4 className={pageUtils.classes.h4}>
									{pageAssets?.request_form_additional_info ||
										'Informations supplémentaires'}
								</h4>
							</div>
						)}

						<div>
							<strong>{incomeSupplementRadio.label}</strong>
							<span
								className={joinClasses([
									pageUtils.classes.answerURB,
									'answerURB'
								])}
							>
								{answers.incomeSupplementRadio
									? pageAssets.label_yes
									: pageAssets.label_no}
							</span>
						</div>

						{answers.incomeSupplementRadio && (
							<>
								<div>
									<strong>{incomeSupplementText.label}</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.incomeSupplementText || 'N/A'}
									</span>
								</div>
								<RequestFiles
									subtitle={incomeSupplementFile.label}
									name={incomeSupplementFile.name}
									required={false}
									files={incomeSupplementFile.files}
									hasError={false}
									onSetHasError={() => {}}
									onSetFiles={() => {}}
									maxFiles={incomeSupplementFile.numberFilesRequired}
									removePadding
									isSummary={true}
								/>
								<br />
							</>
						)}

						<div>
							<strong>{details3CoBeneficiaryRadio.label}</strong>
							<span
								className={joinClasses([
									pageUtils.classes.answerURB,
									'answerURB'
								])}
							>
								{answers.coBeneficiaryRadio
									? pageAssets.label_yes
									: pageAssets.label_no}
							</span>
						</div>

						{answers.coBeneficiaryRadio && (
							<>
								<div>
									<strong>{details3CoBeneficiaryName.label}</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.coBeneficiaryFirstName || 'N/A'}
									</span>
								</div>
								<div>
									<strong>{details3CoBeneficiaryRelation.label}</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.coBeneficiaryLastName || 'N/A'}
									</span>
								</div>
								<div>
									<strong>{details3CoBeneficiaryDate.label}</strong>
									<span
										className={joinClasses([
											pageUtils.classes.answerURB,
											'answerURB'
										])}
									>
										{answers.coBeneficiaryDate || 'N/A'}
									</span>
								</div>
								<RequestFiles
									subtitle={details3CoBeneficiaryFile.label}
									name={details3CoBeneficiaryFile.name}
									required={false}
									files={details3CoBeneficiaryFile.files}
									hasError={false}
									onSetHasError={() => {}}
									onSetFiles={() => {}}
									maxFiles={details3CoBeneficiaryFile.numberFilesRequired}
									removePadding
									isSummary={true}
								/>
								<br />
							</>
						)}
					</>
				)}
			</section>
		)
	}

	return (
		<>
			<ReactCollapse
				isOpened={
					subSteps !== undefined &&
					subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
				}
			>
				<section>
					<Notification
						text={formatStrapiText(
							pageAssets?.request_form_elderSubvention_intro_notification
						)}
						type="info"
						hasHtml
					/>

					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_elderSubvention_firstSubStep_title}
					</h4>

					<RadioInput
						name={firstTimeSubmitInput.name}
						label={firstTimeSubmitInput.label}
						value={firstTimeSubmitInput.value}
						list={radioListFirstTimeSubmit}
						direction={Direction.vertical}
						hasError={errors!.includes(firstTimeSubmitInput.name)}
						onChange={onSelectFirstTimeSubmitInput}
						ref={firstTimeSubmitInput.ref as RefObject<HTMLInputElement>}
						required={firstTimeSubmitInput.required}
						hasBackgroundWrapper
					/>

					{firstTimeSubmitInput.value === 'true' ? (
						<>
							<TextInput
								id={clientNumberInput.name}
								label={clientNumberInput.label}
								value={clientNumberInput.value}
								required={clientNumberInput.required}
								hasError={errors.includes(clientNumberInput.name)}
								onChange={onSelectClientNumberInput}
							/>
							<p style={{ marginTop: '15px', marginBottom: '10px' }}>
								{
									pageAssets?.request_form_elderSubvention_clientNumber_helper_text
								}
							</p>
							<div className={classes.clientNumberExampleImageWrapper}>
								<img
									src={elderSubventionNumClientExample}
									alt="Exemple numéro de client"
									className={classes.clientNumberExampleImage}
								/>
							</div>
						</>
					) : (
						firstTimeSubmitInput.value === 'false' && (
							<>
								<DateFilterInput
									id={clientAgeInput.name}
									label={clientAgeInput.label}
									value={clientAgeInput.value}
									description={clientAgeInput.description}
									onChange={onSelectClientAgeInput}
									hasError={errors!.includes(clientAgeInput.name)}
									required={clientAgeInput.required}
									dateFormat="dd/MM/yyyy"
									maxDate={maxDateFor65Years}
									shouldDisplayCustomHeader
									useSeniorYearsList
								/>
								<RequestFiles
									subtitle={proofOfBirthInput.label}
									name={proofOfBirthInput.name}
									required={proofOfBirthInput.required}
									files={proofOfBirthInput.files}
									hasError={errors.includes(proofOfBirthInput.name)}
									removeSectionMarginTop
									onSetHasError={onSetErrorFromFileComponent}
									onSetFiles={onSelectProofOfBirthInput}
									maxFiles={proofOfBirthInput.numberFilesRequired}
									removePadding
								/>
							</>
						)
					)}
				</section>
			</ReactCollapse>

			<ReactCollapse
				isOpened={
					subSteps !== undefined &&
					subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
				}
			>
				<section>
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_parkingPermit_address_of_request}
					</h4>

					{firstTimeSubmitInput.value === 'true' && (
						<RadioInput
							name={clientCorrectAddressInput.name}
							label={clientCorrectAddressInput.label}
							value={clientCorrectAddressInput.value}
							list={radioListYesOrNo}
							direction={Direction.vertical}
							hasError={errors.includes(clientCorrectAddressInput.name)}
							onChange={onSelectClientCorrectAddressInput}
							ref={clientCorrectAddressInput.ref as RefObject<HTMLInputElement>}
							required={clientCorrectAddressInput.required}
							hasBackgroundWrapper
						/>
					)}

					<ReactCollapse
						isOpened={
							`${clientCorrectAddressInput.value}` === 'false' ||
							firstTimeSubmitInput.value === 'false'
						}
					>
						<div style={{ marginBottom: '20px' }}>
							<ConcernedInfosSection
								errors={errors}
								inputs={inputs}
								onFixError={onFixError}
								onSetInputs={onSetInputs}
								fieldsAlternativeLabel={{
									concernedAdresse:
										firstTimeSubmitInput.value === 'false'
											? pageAssets?.request_form_parkingPermit_concerned_address_label
											: undefined
								}}
								isConcernedInfosRequired={
									`${clientCorrectAddressInput.value}` === 'false' ||
									firstTimeSubmitInput.value === 'false'
								}
								requiredFields={[
									ConcernFieldNames.concernedAdresse,
									ConcernFieldNames.concernedAppartement,
									ConcernFieldNames.concernedCoordinates
								]}
								onGetCadastralAndMatriculeNumber={
									getCadastralAndMatriculeNumber
								}
								subStep={REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2}
							/>
						</div>
					</ReactCollapse>

					{`${clientCorrectAddressInput.value}` == 'false' &&
						firstTimeSubmitInput.value === 'true' && (
							<DateFilterInput
								id={moveDateInput.name}
								label={moveDateInput.label}
								value={moveDateInput.value}
								onChange={onSelectMoveDateInput}
								hasError={errors!.includes(moveDateInput.name)}
								required={moveDateInput.required}
								dateFormat="dd/MM/yyyy"
							/>
						)}

					<RadioInput
						name={isClientHouseOwnerInput.name}
						label={isClientHouseOwnerInput.label}
						value={isClientHouseOwnerInput.value}
						list={radioListYesOrNo}
						direction={Direction.vertical}
						hasError={errors.includes(isClientHouseOwnerInput.name)}
						onChange={onSelectIsClientHouseOwnerInput}
						ref={isClientHouseOwnerInput.ref as RefObject<HTMLInputElement>}
						required={isClientHouseOwnerInput.required}
						hasBackgroundWrapper
					/>
					{(clientCorrectAddressInput.value === 'false' ||
						isClientHouseOwnerInput.value === 'false' ||
						firstTimeSubmitInput.value === 'false') && (
						<RequestFiles
							subtitle={proofOfResidencyInput.label}
							name={proofOfResidencyInput.name}
							required={proofOfResidencyInput.required}
							files={proofOfResidencyInput.files}
							description={proofOfResidencyInput.description}
							hasError={errors.includes(proofOfResidencyInput.name)}
							onSetHasError={onSetErrorFromFileComponent}
							onSetFiles={onSelectProofOfResidencyInput}
							maxFiles={proofOfResidencyInput.numberFilesRequired}
							removePadding
						/>
					)}

					{firstTimeSubmitInput.value === 'true' ? (
						<>
							{isClientHouseOwnerInput.value === 'true' ? (
								<RequestFiles
									subtitle={renewalFormOwner.label}
									name={renewalFormOwner.name}
									required={renewalFormOwner.required}
									files={renewalFormOwner.files}
									description={renewalFormOwner.description}
									hasError={errors.includes(renewalFormOwner.name)}
									onSetHasError={onSetErrorFromFileComponent}
									onSetFiles={onSelectRenewalFormOwner}
									maxFiles={renewalFormOwner.numberFilesRequired}
									removePadding
								/>
							) : (
								isClientHouseOwnerInput.value === 'false' && (
									<RequestFiles
										subtitle={renewalFormRenter.label}
										name={renewalFormRenter.name}
										required={renewalFormRenter.required}
										files={renewalFormRenter.files}
										description={renewalFormRenter.description}
										hasError={errors.includes(renewalFormRenter.name)}
										onSetHasError={onSetErrorFromFileComponent}
										onSetFiles={onSelectRenewalFormRenter}
										maxFiles={renewalFormRenter.numberFilesRequired}
										removePadding
									/>
								)
							)}
						</>
					) : (
						<>
							{isClientHouseOwnerInput.value === 'false' && (
								<RequestFiles
									subtitle={proofOfOccupation.label}
									name={proofOfOccupation.name}
									required={proofOfOccupation.required}
									files={proofOfOccupation.files}
									description={proofOfOccupation.description}
									hasError={errors.includes(proofOfOccupation.name)}
									onSetHasError={onSetErrorFromFileComponent}
									onSetFiles={onSelectProofOfOccupation}
									maxFiles={proofOfOccupation.numberFilesRequired}
									removePadding
								/>
							)}
						</>
					)}
				</section>
			</ReactCollapse>

			<ReactCollapse
				isOpened={
					subSteps !== undefined &&
					subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
				}
			>
				<section>
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_additional_info ||
							'Informations supplémentaires'}
					</h4>

					<RadioInput
						name={incomeSupplementRadio.name}
						label={incomeSupplementRadio.label}
						value={incomeSupplementRadio.value}
						list={radioListIncomeSupplement}
						direction={Direction.vertical}
						hasError={errors.includes(incomeSupplementRadio.name)}
						onChange={onSelectIncomeSupplementRadio}
						ref={incomeSupplementRadio.ref as RefObject<HTMLInputElement>}
						required={incomeSupplementRadio.required}
						hasBackgroundWrapper
					/>

					{incomeSupplementRadio.value === 'true' && (
						<>
							<TextInput
								id={incomeSupplementText.name}
								label={incomeSupplementText.label}
								value={incomeSupplementText.value}
								required={incomeSupplementText.required}
								hasError={errors.includes(incomeSupplementText.name)}
								customErrorText={incomeSupplementTextCustomError}
								onChange={onSelectIncomeSupplementText}
							/>

							<RequestFiles
								subtitle={incomeSupplementFile.label}
								name={incomeSupplementFile.name}
								required={incomeSupplementFile.required}
								files={incomeSupplementFile.files}
								description={incomeSupplementFile.description}
								hasError={errors.includes(incomeSupplementFile.name)}
								removeSectionMarginTop
								onSetHasError={onSetErrorFromFileComponent}
								onSetFiles={onSelectIncomeSupplementFile}
								maxFiles={incomeSupplementFile.numberFilesRequired}
								removePadding
							/>
						</>
					)}
					<br />
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_elderSubvention_co_beneficiary_title}
					</h4>

					<Notification
						text={
							pageAssets?.request_form_elderSubvention_co_beneficiary_notification
						}
						type="info"
						hasHtml
					/>

					<RadioInput
						name={details3CoBeneficiaryRadio.name}
						label={details3CoBeneficiaryRadio.label}
						value={details3CoBeneficiaryRadio.value}
						list={radioListYesOrNo}
						direction={Direction.vertical}
						hasError={errors.includes(details3CoBeneficiaryRadio.name)}
						onChange={onSelectDetails3CoBeneficiaryRadio}
						ref={details3CoBeneficiaryRadio.ref as RefObject<HTMLInputElement>}
						required={details3CoBeneficiaryRadio.required}
						hasBackgroundWrapper
					/>

					{details3CoBeneficiaryRadio.value === 'true' && (
						<>
							<TextInput
								id={details3CoBeneficiaryName.name}
								label={details3CoBeneficiaryName.label}
								value={details3CoBeneficiaryName.value}
								required={details3CoBeneficiaryName.required}
								hasError={errors.includes(details3CoBeneficiaryName.name)}
								onChange={onSelectDetails3CoBeneficiaryName}
							/>

							<TextInput
								id={details3CoBeneficiaryRelation.name}
								label={details3CoBeneficiaryRelation.label}
								value={details3CoBeneficiaryRelation.value}
								required={details3CoBeneficiaryRelation.required}
								hasError={errors.includes(details3CoBeneficiaryRelation.name)}
								onChange={onSelectDetails3CoBeneficiaryRelation}
							/>

							<DateFilterInput
								id={details3CoBeneficiaryDate.name}
								label={details3CoBeneficiaryDate.label}
								value={details3CoBeneficiaryDate.value}
								onChange={onSelectDetails3CoBeneficiaryDate}
								hasError={errors!.includes(details3CoBeneficiaryDate.name)}
								required={details3CoBeneficiaryDate.required}
								dateFormat="dd/MM/yyyy"
								maxDate={maxDateFor65Years}
								shouldDisplayCustomHeader
								useSeniorYearsList
							/>

							<RequestFiles
								subtitle={details3CoBeneficiaryFile.label}
								name={details3CoBeneficiaryFile.name}
								required={details3CoBeneficiaryFile.required}
								files={details3CoBeneficiaryFile.files}
								description={details3CoBeneficiaryFile.description}
								hasError={errors.includes(details3CoBeneficiaryFile.name)}
								removeSectionMarginTop
								onSetHasError={onSetErrorFromFileComponent}
								onSetFiles={onSelectDetails3CoBeneficiaryFile}
								maxFiles={details3CoBeneficiaryFile.numberFilesRequired}
								removePadding
							/>
						</>
					)}
				</section>
			</ReactCollapse>
		</>
	)
}
export default ElderSubventionRenewalForm
